import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  X, Download, Printer, ZoomIn, ZoomOut, Maximize2, ExternalLink,
  AlertTriangle, FileText, Loader2, ChevronLeft, ChevronRight
} from 'lucide-react';
import { cn } from '../../lib/utils';

const PDF_MIME = 'application/pdf';
const IMAGE_MIMES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif', 'image/svg+xml'];
const TEXT_MIMES = ['text/plain', 'text/html', 'application/json', 'text/csv'];

const guessMimeFromFilename = (filename = '') => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return PDF_MIME;
  if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
  if (ext === 'svg') return 'image/svg+xml';
  if (['txt', 'log'].includes(ext)) return 'text/plain';
  if (ext === 'json') return 'application/json';
  if (ext === 'html') return 'text/html';
  if (ext === 'csv') return 'text/csv';
  return null;
};

const formatBytes = (bytes) => {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
};

export default function GEDDocumentViewerModal({
  doc, onClose, onDownload, previewDocument,
  documents = [], onNavigate
}) {
  const [zoom, setZoom] = useState(100);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [printing, setPrinting] = useState(false);

  const filename = doc.original_filename || doc.filename || 'document';
  const mimeType = doc.mime_type || guessMimeFromFilename(filename) || PDF_MIME;
  const isPdf = mimeType === PDF_MIME || filename.toLowerCase().endsWith('.pdf');
  const isImage = IMAGE_MIMES.includes(mimeType);
  const isText = TEXT_MIMES.includes(mimeType);

  // Navigation
  const currentIndex = documents.findIndex(d => d.id === doc.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < documents.length - 1;

  // Garde une référence stable de previewDocument pour éviter les re-fetch
  // en boucle quand le parent ne mémoïse pas le callback (re-render parent
  // → nouvelle référence → useEffect re-trigger → re-download blob → flicker).
  const previewDocumentRef = useRef(previewDocument);
  useEffect(() => { previewDocumentRef.current = previewDocument; }, [previewDocument]);

  // Charger le blob — déclenché uniquement quand le doc change
  useEffect(() => {
    let revokedUrl = null;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setBlobUrl(null);

    (async () => {
      try {
        const url = await previewDocumentRef.current(doc.id);
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        revokedUrl = url;
        setBlobUrl(url);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.message || 'Impossible de charger le document');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [doc.id]);

  // Raccourcis clavier
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && hasPrev && onNavigate) onNavigate(documents[currentIndex - 1]);
    if (e.key === 'ArrowRight' && hasNext && onNavigate) onNavigate(documents[currentIndex + 1]);
    if (e.key === '+' || e.key === '=') setZoom(z => Math.min(300, z + 25));
    if (e.key === '-') setZoom(z => Math.max(25, z - 25));
    if (e.key === '0') setZoom(100);
  }, [onClose, hasPrev, hasNext, onNavigate, documents, currentIndex]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const openInNewTab = () => { if (blobUrl) window.open(blobUrl, '_blank'); };

  const handlePrint = async () => {
    if (!blobUrl) return;
    setPrinting(true);
    try {
      const printWindow = window.open(blobUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          try { printWindow.print(); } catch { /* user blocked */ }
        };
      }
    } finally {
      setTimeout(() => setPrinting(false), 1500);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 md:p-6 animate-in fade-in duration-200"
      data-testid="ged-viewer-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-white w-full max-w-7xl h-full rounded-xl shadow-2xl flex flex-col overflow-hidden ring-1 ring-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-3 min-w-0">
            <div className={cn(
              'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
              isPdf ? 'bg-red-100 text-red-600' : isImage ? 'bg-violet-100 text-violet-600' : 'bg-blue-100 text-blue-600'
            )}>
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-900 truncate" title={filename} data-testid="viewer-filename">
                {filename}
              </h3>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-mono uppercase">{mimeType.split('/')[1] || mimeType}</Badge>
                {doc.size && <span className="text-[11px] text-slate-500">{formatBytes(doc.size)}</span>}
                {doc.source && <Badge variant="secondary" className="text-[10px] py-0 px-1.5">{doc.source}</Badge>}
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Navigation prev/next */}
            {documents.length > 1 && onNavigate && (
              <>
                <Button variant="ghost" size="sm" onClick={() => onNavigate(documents[currentIndex - 1])}
                  disabled={!hasPrev} className="h-8 w-8 p-0" data-testid="viewer-prev-btn"
                  title="Document précédent (←)">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-slate-500 px-1 hidden sm:inline">
                  {currentIndex + 1}/{documents.length}
                </span>
                <Button variant="ghost" size="sm" onClick={() => onNavigate(documents[currentIndex + 1])}
                  disabled={!hasNext} className="h-8 w-8 p-0" data-testid="viewer-next-btn"
                  title="Document suivant (→)">
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <div className="w-px h-5 bg-slate-200 mx-1" />
              </>
            )}

            {/* Zoom (image uniquement — désactivé pour PDF car blob:URL natif ne le supporte pas) */}
            {isImage && (
              <div className="hidden sm:flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.max(25, z - 25))}
                  className="h-8 w-8 p-0" title="Zoom arrière (-)" data-testid="viewer-zoom-out-btn">
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-xs font-mono text-slate-600 w-12 text-center" data-testid="viewer-zoom-value">{zoom}%</span>
                <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.min(300, z + 25))}
                  className="h-8 w-8 p-0" title="Zoom avant (+)" data-testid="viewer-zoom-in-btn">
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setZoom(100)}
                  className="h-8 w-8 p-0" title="Taille réelle (0)" data-testid="viewer-zoom-reset-btn">
                  <Maximize2 className="h-4 w-4" />
                </Button>
                <div className="w-px h-5 bg-slate-200 mx-1" />
              </div>
            )}

            {/* Actions */}
            {blobUrl && (
              <Button variant="ghost" size="sm" onClick={openInNewTab}
                className="h-8 px-2 gap-1.5 text-slate-600 hover:text-blue-700"
                title="Ouvrir dans un nouvel onglet" data-testid="viewer-newtab-btn">
                <ExternalLink className="h-4 w-4" />
                <span className="hidden md:inline text-xs">Nouvel onglet</span>
              </Button>
            )}

            {isPdf && blobUrl && (
              <Button variant="ghost" size="sm" onClick={handlePrint} disabled={printing}
                className="h-8 px-2 gap-1.5 text-slate-600 hover:text-blue-700"
                title="Imprimer" data-testid="viewer-print-btn">
                {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                <span className="hidden md:inline text-xs">Imprimer</span>
              </Button>
            )}

            {onDownload && (
              <Button variant="outline" size="sm" onClick={() => onDownload(doc)}
                className="h-8 px-2.5 gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                title="Télécharger" data-testid="viewer-download-btn">
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline text-xs">Télécharger</span>
              </Button>
            )}

            <div className="w-px h-5 bg-slate-200 mx-1 hidden sm:block" />
            <Button variant="ghost" size="sm" onClick={onClose}
              className="h-8 w-8 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
              title="Fermer (Esc)" data-testid="viewer-close-btn">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center p-0 sm:p-4">
          {loading && (
            <div className="text-center" data-testid="viewer-loading">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600">Chargement du document...</p>
            </div>
          )}

          {loadError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center" data-testid="viewer-error">
              <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
              <h4 className="font-semibold text-red-800 mb-1">Erreur de chargement</h4>
              <p className="text-sm text-red-700 mb-4">{loadError}</p>
              {onDownload && (
                <Button onClick={() => onDownload(doc)} size="sm" className="bg-red-600 hover:bg-red-700">
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger directement
                </Button>
              )}
            </div>
          )}

          {!loading && !loadError && blobUrl && (
            <>
              {isPdf && (
                <object
                  data={`${blobUrl}#toolbar=0&navpanes=0`}
                  type="application/pdf"
                  className="w-full h-full bg-white shadow-md rounded"
                  aria-label={filename}
                  data-testid="viewer-pdf-iframe"
                >
                  {/* Fallback : navigateurs qui ne supportent pas <object data=blob:> */}
                  <embed
                    src={`${blobUrl}#toolbar=0&navpanes=0`}
                    type="application/pdf"
                    className="w-full h-full bg-white shadow-md rounded"
                  />
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 max-w-md text-center mx-auto mt-12">
                    <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                    <h4 className="font-semibold text-amber-800 mb-1">Aperçu PDF indisponible</h4>
                    <p className="text-sm text-amber-700 mb-4">
                      Votre navigateur ne peut pas afficher ce PDF en mode aperçu.
                    </p>
                    <div className="flex gap-2 justify-center">
                      <Button onClick={openInNewTab} size="sm" variant="outline">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Ouvrir dans un nouvel onglet
                      </Button>
                      {onDownload && (
                        <Button onClick={() => onDownload(doc)} size="sm" className="bg-amber-600 hover:bg-amber-700">
                          <Download className="h-4 w-4 mr-2" />
                          Télécharger
                        </Button>
                      )}
                    </div>
                  </div>
                </object>
              )}
              {isImage && (
                <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
                  <img
                    src={blobUrl}
                    alt={filename}
                    style={{ width: `${zoom}%`, height: 'auto', maxWidth: 'none' }}
                    className="bg-white shadow-md rounded transition-all"
                    data-testid="viewer-image"
                  />
                </div>
              )}
              {isText && (
                <iframe
                  src={blobUrl}
                  className="w-full h-full bg-white shadow-md rounded"
                  title={filename}
                  data-testid="viewer-text-iframe"
                />
              )}
              {!isPdf && !isImage && !isText && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 max-w-md text-center">
                  <FileText className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                  <h4 className="font-semibold text-amber-800 mb-1">Aperçu non disponible</h4>
                  <p className="text-sm text-amber-700 mb-4">
                    Ce type de fichier ({mimeType}) ne peut pas être prévisualisé.
                  </p>
                  {onDownload && (
                    <Button onClick={() => onDownload(doc)} size="sm" className="bg-amber-600 hover:bg-amber-700">
                      <Download className="h-4 w-4 mr-2" />
                      Télécharger
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-[11px] text-slate-500">
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline">Esc : fermer</span>
            {(isPdf || isImage) && <span className="hidden sm:inline">{isImage ? '+/− : zoom' : 'PDF natif'}</span>}
            {documents.length > 1 && <span className="hidden md:inline">←/→ : navigation</span>}
          </div>
          <div className="font-mono">{doc.id?.slice(0, 8)}</div>
        </div>
      </div>
    </div>
  );
}
